import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

const SHOPIFY_STORE = 'https://gifts.charmingcreationsgifts.com';
const CART_STORAGE_KEY = 'charming_creations_cart';

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [notification, setNotification] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse cart:', e);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  // Add item to cart
  const addToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      const existingItem = prev.find(item => item.id === product.id);
      if (existingItem) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
    
    // Show notification
    setNotification({
      message: `${product.name} added to cart!`,
      type: 'success'
    });
    setTimeout(() => setNotification(null), 3000);
  };

  // Remove item from cart
  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  };

  // Update quantity
  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
  };

  // Get cart total
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Get total items count
  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Generate Shopify checkout URL
  const getCheckoutUrl = () => {
    if (cartItems.length === 0) return SHOPIFY_STORE;
    
    // Build cart URL with variant IDs
    // Format: /cart/VARIANT_ID:QUANTITY,VARIANT_ID:QUANTITY
    const cartString = cartItems
      .filter(item => item.variant_id) // Only items with variant IDs
      .map(item => `${item.variant_id}:${item.quantity}`)
      .join(',');
    
    if (cartString) {
      return `${SHOPIFY_STORE}/cart/${cartString}`;
    }
    
    // Fallback: redirect to store
    return `${SHOPIFY_STORE}/collections/all`;
  };

  // Proceed to checkout
  const checkout = () => {
    const checkoutUrl = getCheckoutUrl();
    window.open(checkoutUrl, '_blank');
  };

  const value = {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    notification,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    getCheckoutUrl,
    checkout
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
